import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { setAIAssistantDisplayed } from '@/aiAssistant/aiAssistant.actions';
import { getDefaultDisplayed } from '@/aiAssistant/aiAssistant.utilities';
import { subscribeToAuthChangeChannel } from '@/homescreen/homescreen.actions';
import { LoadingFallback } from '@/main/LoadingFallback.atom';
import { StoreProviders } from '@/main/StoreProviders';
import { cleanupOnUnload } from '@/main/cleanup.utilities';
import { setNavigate } from '@/main/routing.utilities';
import { isEmbeddedView } from '@/main/routing.utilities';
import { cancelRequestsOnUnload } from '@/requests/pendingRequests.utilities';
import { cleanupBroadcastChannels } from '@/services/broadcastChannel.utilities';
import { addEventListeners } from '@/services/notifier.service';
import { fetchConfiguration } from '@/services/systemConfiguration.utilities';
import { SystemMessageListener } from '@/systemWarning/SystemMessageListener';
import { Tracker } from '@/track/Tracker.atom';
import { init18n } from '@/utilities/i18n.utilities';

/**
 * Wrapper component that wraps all the Route components.
 * Everything that has to happen once per application load and applies to every single component should happen here.
 */
export const Main = () => {
  const navigate = useNavigate();
  const [configLoaded, setConfigLoaded] = useState(false);
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);

  useEffect(() => {
    fetchConfiguration().then(() => {
      init18n()
        .then(() => {
          setIsI18nInitialized(true);
        })
        .catch((error) => {
          console.error('Error initializing i18n: ', error);
        });

      setConfigLoaded(true);
      if (!isEmbeddedView()) {
        setAIAssistantDisplayed(getDefaultDisplayed(), true);
      }
    });
    subscribeToAuthChangeChannel();
    addEventListeners();

    // we need to expose the navigate function to call it from services
    setNavigate(navigate);
    const beforeUnloadListener = () => cancelRequestsOnUnload();
    window.addEventListener('beforeunload', beforeUnloadListener);
    return () => {
      cleanupBroadcastChannels();
      cancelRequestsOnUnload();
      cleanupOnUnload();
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, []);

  if (!configLoaded || !isI18nInitialized) {
    return <LoadingFallback />;
  }

  return (
    <StoreProviders>
      <SystemMessageListener />
      <Tracker />
      <Outlet />
    </StoreProviders>
  );
};
